import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { omit } from 'remeda';

import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { ConversationStatus, useTwilioConversationCallbacks } from '../../session/chat/useTwilioConversationCallbacks';
import { currentConversationParticipantIdsAtom, currentConversationSidAtom } from '../../session/state';
import { parseIdentity } from '../../video-sdk';
import {
  ProviderDrawerState,
  drawerStateAtom,
  joiningVisitorIdsAtom,
  sessionConversationReadyAtom,
  sessionConversationSidAtom,
  visitorPresenceAtom,
  waitingRoomConversationSidsByVisitorIdAtom,
} from '../state';
import { useGetVisitorChatUser } from './useGetVisitorChatParticipant';
import { useShowNewMessageToastCallback } from './useShowNewMessageToastCallback';

function useHandleConversationOnClientDisconnectCallback() {
  const getCurrentConversationParticipantIds = useAtomCallbackMemoized(
    (get) => get(currentConversationParticipantIdsAtom),
    [],
  );
  const getDrawerState = useAtomCallbackMemoized((get) => get(drawerStateAtom), []);
  const getSessionConversationReady = useAtomCallbackMemoized((get) => get(sessionConversationReadyAtom), []);
  const getSessionConversationSid = useAtomCallbackMemoized((get) => get(sessionConversationSidAtom), []);
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setCurrentConversationSid = useSetAtom(currentConversationSidAtom);
  const setWaitingRoomConversationSids = useSetAtom(waitingRoomConversationSidsByVisitorIdAtom);

  return useCallback(
    (visitorId: string) => {
      const isVisitorInCurrentConvo = Array.from(getCurrentConversationParticipantIds()).some(
        (participantId) => parseIdentity(participantId).id === visitorId,
      );

      const isWaitingRoomChat = getDrawerState() === ProviderDrawerState.WAITING_ROOM_CHAT;

      // handle if we are simply transitioning from waiting room -> session chat
      // otherwise handle the participant disconnecting
      if (isWaitingRoomChat && isVisitorInCurrentConvo) {
        if (getSessionConversationReady()) {
          setDrawerState(ProviderDrawerState.SESSION_CHAT);
          setCurrentConversationSid(getSessionConversationSid());
        } else {
          setDrawerState(ProviderDrawerState.PARTICIPANT_DISCONNECTED_CHAT);
          setCurrentConversationSid(undefined);
        }
      }

      setWaitingRoomConversationSids((prev) => omit(prev, [visitorId]));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

function useHandleChatParticipantListenersCallback() {
  const setVisitors = useSetAtom(visitorPresenceAtom);
  const getJoiningVisitorIds = useAtomCallbackMemoized((get) => get(joiningVisitorIdsAtom), []);
  const getVisitorChatUser = useGetVisitorChatUser();
  const handleConversationOnClientDisconnect = useHandleConversationOnClientDisconnectCallback();

  return useCallback(
    async (visitorId: string) => {
      const visitorUser = await getVisitorChatUser(visitorId);
      visitorUser?.on('updated', ({ user }) => {
        if (user.isOnline) return;
        handleConversationOnClientDisconnect(visitorId);
        if (!getJoiningVisitorIds().has(visitorId)) {
          setVisitors((prev) => omit(prev, [visitorId]));
        }
        visitorUser.removeAllListeners('updated');
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

export function useWaitingRoomConversationCallbacks() {
  const { joinConversation, leaveConversation } = useTwilioConversationCallbacks();
  const setWaitingRoomConversationSids = useSetAtom(waitingRoomConversationSidsByVisitorIdAtom);
  const getCurrentConversationSid = useAtomCallbackMemoized((get) => get(currentConversationSidAtom), []);
  const setCurrentConversationSid = useSetAtom(currentConversationSidAtom);
  const showNewMessageToast = useShowNewMessageToastCallback();
  const getDrawerState = useAtomCallbackMemoized((get) => get(drawerStateAtom), []);
  const handleChatParticipantListeners = useHandleChatParticipantListenersCallback();

  return useMemo(
    () => ({
      joinWaitingRoomConversation: async (visitorId: string, waitingRoomConversationSid: string) => {
        const conversationResult = await joinConversation(waitingRoomConversationSid, {
          onMessageAdded: (message) => {
            showNewMessageToast(message, ProviderDrawerState.WAITING_ROOM_CHAT);
          },
        });

        const successfullyJoinedConvo =
          conversationResult.status === 'success' && conversationResult.code === ConversationStatus.JOINED;
        if (successfullyJoinedConvo) {
          setWaitingRoomConversationSids((prev) => ({
            ...prev,
            [visitorId]: waitingRoomConversationSid,
          }));
          const drawerState = getDrawerState();
          const currentConversationSid = getCurrentConversationSid();
          // if the chat drawer is open in an empty state, set it to the waiting room conversation
          if (drawerState === ProviderDrawerState.WAITING_ROOM_CHAT && !currentConversationSid) {
            setCurrentConversationSid(waitingRoomConversationSid);
          }
          await handleChatParticipantListeners(visitorId);
        }
        return conversationResult;
      },
      leaveWaitingRoomConversation: async (visitorId: string, waitingRoomConversationSid: string) => {
        const conversationResult = await leaveConversation(waitingRoomConversationSid);
        if (conversationResult.status === 'success') {
          setWaitingRoomConversationSids((prev) => omit(prev, [visitorId]));
        }
        return conversationResult;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
